import React, { useMemo, useState } from "react";
import {
  Card,
  CardActions,
  CardHeader,
  CardContent,
  makeStyles,
  Typography,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@material-ui/core";

import { useAlert } from "react-alert";

import { WebTracker } from "../../types";
import { WebTrackerFollowUpTask } from "./FollowUpTasksTypes";
import {
  RequestableSaasDestination,
  SaaSDestination,
} from "../../../destinations/destinations";
import { useAllSaasDestinations } from "../../../hooks/useAllSaasDestinations";
import {
  getExtraStepsToReplaceWithFreshpaintDestination,
  getExtraStepsToReplaceWithFreshpaintVideoProxy,
  getExtraStepsToSetUpFreshpaintMaps,
} from "./FollowUpTaskExtraSteps";
import useConfirmFreshpaintIntegration from "../../mutations/useConfirmFreshpaintIntegration";

const useStyles = makeStyles((theme) => ({
  card: {
    position: "relative",
    minWidth: 500,
    maxWidth: 600,
    paddingTop: 0,
    paddingBottom: 0,
  },
  cardActions: {
    alignContent: "center",
    justifyContent: "right",
    marginTop: theme.spacing(1),
  },
  cancelBtn: {
    marginRight: theme.spacing(2),
  },
  subTitle: {
    marginTop: theme.spacing(1.8),
  },
  listItem: {
    padding: 0,
  },
  indentedListItem: {
    padding: 0,
    paddingLeft: theme.spacing(2),
  },
}));

export type SetupIntegrationProps = {
  onClose: () => void;
  siteDomain: string;
  trackerDomain: string;
  tracker: WebTracker;
  followUpTask: WebTrackerFollowUpTask | null;
};

function getExtraSteps(
  followUpTask: WebTrackerFollowUpTask,
  destination: SaaSDestination | RequestableSaasDestination | undefined,
  tracker: WebTracker
) {
  switch (followUpTask) {
    case "set_up_destination":
      return getExtraStepsToReplaceWithFreshpaintDestination(
        destination as SaaSDestination
      );
    case "set_up_video_proxy":
      return getExtraStepsToReplaceWithFreshpaintVideoProxy(tracker.name);
    case "set_up_fp_maps":
      return getExtraStepsToSetUpFreshpaintMaps(tracker.name);
    default:
      throw new Error(`Invalid follow-up task: ${followUpTask}`);
  }
}

function SetupIntegration({
  onClose,
  siteDomain,
  trackerDomain,
  tracker,
  followUpTask,
}: SetupIntegrationProps) {
  const classes = useStyles();
  const alert = useAlert();

  const [isChecked, setChecked] = useState(false);

  const allSaasDestinations = useAllSaasDestinations();

  const destination = useMemo(
    () => allSaasDestinations[tracker.destinationSlug],
    [allSaasDestinations, tracker.destinationSlug]
  );

  const steps = getExtraSteps(followUpTask!, destination, tracker);

  const onConfirmSuccess = () => {
    alert.info("Confirmed");
    onClose();
  };
  const onConfirmError = () => {
    alert.error("Something went wrong");
  };
  const confirmFPIntegrationMutation = useConfirmFreshpaintIntegration(
    onConfirmSuccess,
    onConfirmError
  );
  const onClickConfirmHandler = () => {
    confirmFPIntegrationMutation.mutate({
      siteDomain,
      trackerDomain,
      trackerName: tracker.name,
    });
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        disableTypography
        title={
          <Typography component="h5" variant="h5">
            Setup Your Freshpaint Integration for {tracker.name}
          </Typography>
        }
        subheader={
          <Typography
            className={classes.subTitle}
            component="p"
            variant="subtitle2"
          >
            Complete the following steps, and then confirm that your integration
            is setup. Once confirmed, this action item will be marked completed.
          </Typography>
        }
      />
      <CardContent className={classes.card}>
        <Grid container className={classes.card}>
          <Grid item>
            <List className="p-0">
              {steps.map((step, index) => (
                <ListItem
                  key={`tracker-resolution-extra-step-${index}`}
                  className={classes.indentedListItem}
                >
                  <ListItemText
                    primary={
                      steps.length > 1 ? [`${index + 1}.  `, step] : step
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => setChecked(e.target.checked)}
                  />
                }
                label="All set! I have completed all the steps above to set up my Freshpaint integration."
              />
            </FormGroup>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Grid container className={classes.cardActions}>
          <Grid item alignItems="flex-end">
            <Button
              className={classes.cancelBtn}
              variant="text"
              color="secondary"
              onClick={onClose}
              size="small"
              title="Cancel"
            >
              Cancel
            </Button>
          </Grid>
          <Grid item alignItems="flex-end">
            <Button
              disabled={!isChecked}
              variant="contained"
              color="primary"
              onClick={onClickConfirmHandler}
              size="small"
              title="Confirm"
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
}

export default SetupIntegration;
